import { useEffect, useRef, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { commonGetData, initCommonState } from "../../../../redux/slices/commonSlice";
import { getPayrollSummaryDashboardPath } from "../../../../services/config";
import CustomPagination from "../../../../components/CustomPagination";
import {
  getKpiCards, getPayrollTrend, getComplianceStatus, getCriticalAlerts,
  getStatutoryCompliance, getPayoutDistribution, getQuickInsights,
  getGrossPayrollBreakdown, getEmployeePayrollStatus, getDepartmentPayrollSummary,
  getFilterBranches, getFilterDepartments, getFilterEmployeeTypes,
  withFallback,
} from '../../payrollDB';

// ── JSON fallbacks ────────────────────────────────────────────────────────────
const FALLBACK = {
  kpiCards:                getKpiCards(),
  payrollTrend:            getPayrollTrend(),
  complianceStatus:        getComplianceStatus(),
  criticalAlerts:          getCriticalAlerts(),
  statutoryCompliance:     getStatutoryCompliance(),
  payoutDistribution:      getPayoutDistribution(),
  quickInsights:           getQuickInsights(),
  grossPayrollBreakdown:   getGrossPayrollBreakdown(),
  employeePayrollStatus:   getEmployeePayrollStatus(),
  departmentPayrollSummary:getDepartmentPayrollSummary(),
};

// ── Filter option lists from JSON ─────────────────────────────────────────────
const BRANCHES    = getFilterBranches();
const DEPARTMENTS = getFilterDepartments();
const EMP_TYPES   = getFilterEmployeeTypes();

const PayrollSummary = ({ onNavigate }) => {
  const dispatch    = useDispatch();
  const commonStore = useSelector((state) => state.common);
  const user        = JSON.parse(localStorage.getItem("user_data")) || {};

  const trendChartRef     = useRef(null);
  const breakdownChartRef = useRef(null);

  // ── Live data state — starts from JSON fallback ───────────────────────────
  const [PS, setPS] = useState(FALLBACK);

  // ── Filter state ──────────────────────────────────────────────────────────
  const today = new Date();
  const defaultMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  const [filterMonth,  setFilterMonth]  = useState(defaultMonth);
  const [filterBranch, setFilterBranch] = useState("");
  const [filterDept,   setFilterDept]   = useState("");
  const [filterType,   setFilterType]   = useState("");
  const [filterSearch, setFilterSearch] = useState("");

  // ── Derived: filtered employee rows ──────────────────────────────────────
  const filteredEmployees = useMemo(() => {
    let rows = PS.employeePayrollStatus;
    const q = filterSearch.trim().toLowerCase();
    if (q) {
      rows = rows.filter(r =>
        r.name.toLowerCase().includes(q) ||
        r.id.toLowerCase().includes(q) ||
        r.role.toLowerCase().includes(q)
      );
    }
    // department filter maps to role keyword (best-effort on JSON data)
    if (filterDept) {
      rows = rows.filter(r =>
        r.role.toLowerCase().includes(filterDept.toLowerCase()) ||
        r.id.toLowerCase().startsWith(filterDept.slice(0, 3).toUpperCase())
      );
    }
    return rows;
  }, [PS.employeePayrollStatus, filterSearch, filterDept]);

  // ── Derived: filtered department rows ────────────────────────────────────
  const filteredDepts = useMemo(() => {
    let rows = PS.departmentPayrollSummary;
    if (filterDept) {
      rows = rows.filter(r =>
        r.name.toLowerCase().includes(filterDept.toLowerCase())
      );
    }
    if (filterSearch.trim()) {
      const q = filterSearch.trim().toLowerCase();
      rows = rows.filter(r => r.name.toLowerCase().includes(q));
    }
    return rows;
  }, [PS.departmentPayrollSummary, filterDept, filterSearch]);

  // ── Derived: filtered statutory compliance ────────────────────────────────
  const filteredStatutory = useMemo(() => {
    if (!filterSearch.trim()) return PS.statutoryCompliance;
    const q = filterSearch.trim().toLowerCase();
    return PS.statutoryCompliance.filter(s =>
      s.title.toLowerCase().includes(q) || s.sub.toLowerCase().includes(q)
    );
  }, [PS.statutoryCompliance, filterSearch]);

  // ── Clear all filters ─────────────────────────────────────────────────────
  const clearFilters = () => {
    setFilterMonth(defaultMonth);
    setFilterBranch("");
    setFilterDept("");
    setFilterType("");
    setFilterSearch("");
    setEmpPage(0);
    setDeptPage(0);
  };

  const hasActiveFilter = filterBranch || filterDept || filterType || filterSearch ||
    filterMonth !== defaultMonth;

  // ── Pagination state for employee & department tables ─────────────────────
  const [empPage,     setEmpPage]     = useState(0);
  const [empPageSize, setEmpPageSize] = useState(10);
  const [deptPage,    setDeptPage]    = useState(0);
  const [deptPageSize,setDeptPageSize]= useState(10);

  // Reset to page 0 when filters change
  useEffect(() => { setEmpPage(0); }, [filterSearch, filterDept, filterBranch, filterType]);
  useEffect(() => { setDeptPage(0); }, [filterSearch, filterDept]);

  // ── Paginated slices ──────────────────────────────────────────────────────
  const pagedEmployees = useMemo(() => {
    const start = empPage * empPageSize;
    return filteredEmployees.slice(start, start + empPageSize);
  }, [filteredEmployees, empPage, empPageSize]);

  const pagedDepts = useMemo(() => {
    const start = deptPage * deptPageSize;
    return filteredDepts.slice(start, start + deptPageSize);
  }, [filteredDepts, deptPage, deptPageSize]);

  // ── Fetch payroll summary on mount ────────────────────────────────────────
  useEffect(() => {
    dispatch(commonGetData(
      `${getPayrollSummaryDashboardPath}?userId=${user.user_id}`
    ));
  }, [dispatch, user.user_id]);

  // ── Handle API response ───────────────────────────────────────────────────
  useEffect(() => {
    if (commonStore.commonGetSuccess && commonStore.commonGetData) {
      const api = commonStore.commonGetData;
      setPS({
        kpiCards:                withFallback(FALLBACK.kpiCards,                api.kpiCards),
        payrollTrend:            withFallback(FALLBACK.payrollTrend,            api.payrollTrend),
        complianceStatus:        withFallback(FALLBACK.complianceStatus,        api.complianceStatus),
        criticalAlerts:          withFallback(FALLBACK.criticalAlerts,          api.criticalAlerts),
        statutoryCompliance:     withFallback(FALLBACK.statutoryCompliance,     api.statutoryCompliance),
        payoutDistribution:      withFallback(FALLBACK.payoutDistribution,      api.payoutDistribution),
        quickInsights:           withFallback(FALLBACK.quickInsights,           api.quickInsights),
        grossPayrollBreakdown:   withFallback(FALLBACK.grossPayrollBreakdown,   api.grossPayrollBreakdown),
        employeePayrollStatus:   withFallback(FALLBACK.employeePayrollStatus,   api.employeePayrollStatus),
        departmentPayrollSummary:withFallback(FALLBACK.departmentPayrollSummary,api.departmentPayrollSummary),
      });
      dispatch(initCommonState());
    }
    if (commonStore.commonGetError) {
      dispatch(initCommonState());
    }
  }, [commonStore.commonGetSuccess, commonStore.commonGetError]);

  // Function to get CSS variable values
  const cssVar = (name) =>
    getComputedStyle(document.documentElement).getPropertyValue(name).trim() || "";

  const initTrendChart = () => {
    if (!window.Plotly || !trendChartRef.current) return;
    
    const primary = cssVar("--primary") || "#8660F8";
    const muted = cssVar("--text-muted") || "#A9A3C9";
    const bg = cssVar("--pay-surface") || "#ffffff";
    
    const data = [{
      x: PS.payrollTrend.months,
      y: PS.payrollTrend.values,
      type: "scatter",
      mode: "lines",
      fill: "tozeroy",
      line: { color: primary, width: 3, shape: "spline", smoothing: 1.3 },
      fillcolor: "rgba(134,96,248,0.08)",
      marker: { size: 6, color: bg, line: { color: primary, width: 2 } },
    }];
    
    const layout = {
      margin: { t: 10, r: 10, b: 30, l: 40 },
      plot_bgcolor: "transparent",
      paper_bgcolor: "transparent",
      xaxis: {
        showgrid: false,
        color: muted,
        tickfont: { family: "Outfit, sans-serif", size: 11 }
      },
      yaxis: {
        showgrid: true,
        gridcolor: "rgba(128,128,128,0.1)",
        color: muted,
        tickprefix: "₹",
        ticksuffix: "Cr",
        tickfont: { family: "Outfit, sans-serif", size: 11 }
      },
      hovermode: "x unified",
      showlegend: false,
    };
    
    window.Plotly.newPlot(trendChartRef.current, data, layout, { responsive: true, displayModeBar: false });
  };

  const initBreakdownChart = () => {
    if (!window.Plotly || !breakdownChartRef.current) return;
    
    const muted = cssVar("--text-muted") || "#A9A3C9";
    const textPrimary = cssVar("--text-primary") || "#E3E2EB";
    
    const data = [{
      values: PS.grossPayrollBreakdown.values,
      labels: PS.grossPayrollBreakdown.labels,
      type: "pie",
      hole: 0.5,
      marker: { colors: PS.grossPayrollBreakdown.colors },
      textinfo: "label+percent",
      textfont: { family: "Outfit, sans-serif", size: 12, color: "#FFFFFF" },
      hovertemplate: "<b>%{label}</b><br>₹%{value:,.0f}<br>%{percent}<extra></extra>",
    }];
    
    const layout = {
      margin: { t: 20, r: 20, b: 20, l: 20 },
      plot_bgcolor: "transparent",
      paper_bgcolor: "transparent",
      showlegend: true,
      legend: {
        orientation: "h",
        y: -0.1,
        x: 0.5,
        xanchor: "center",
        font: { family: "Outfit, sans-serif", size: 11, color: muted }
      },
      annotations: [{
        text: "<b>₹1.24Cr</b><br><span style='font-size:11px;font-family:Outfit,sans-serif'>Total</span>",
        x: 0.5,
        y: 0.5,
        font: { size: 13, family: "Outfit, sans-serif", color: textPrimary },
        showarrow: false
      }],
    };
    
    window.Plotly.newPlot(breakdownChartRef.current, data, layout, { responsive: true, displayModeBar: false });
  };

  // Load Plotly and initialize charts
  useEffect(() => {
    const loadScript = (src, id) =>
      new Promise((resolve) => {
        if (document.getElementById(id)) { resolve(); return; }
        const s = document.createElement("script");
        s.src = src;
        s.id = id;
        s.onload = resolve;
        document.head.appendChild(s);
      });

    if (!document.getElementById("ps-plotly-js")) {
      loadScript("https://cdn.plot.ly/plotly-3.1.1.min.js", "ps-plotly-js").then(() => {
        initTrendChart();
        initBreakdownChart();
      });
    } else if (window.Plotly) {
      initTrendChart();
      initBreakdownChart();
    }
  }, []);

  // Re-initialize charts when theme changes
  useEffect(() => {
    const observer = new MutationObserver(() => {
      if (window.Plotly && trendChartRef.current && breakdownChartRef.current) {
        initTrendChart();
        initBreakdownChart();
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme", "class"]
    });

    return () => observer.disconnect();
  }, []);

  // ── Shared table style helpers ───────────────────────────────────────────
  const thStyle = (alignRight = false) => ({
    padding: "10px 18px",
    fontWeight: 600,
    fontSize: 10,
    fontFamily: "var(--font, 'Outfit', sans-serif)",
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    color: "var(--text-muted)",
    textAlign: alignRight ? "right" : "left",
    whiteSpace: "nowrap",
  });

  const tdStyle = (extra = {}) => ({
    padding: "11px 18px",
    fontSize: 12,
    fontFamily: "var(--font, 'Outfit', sans-serif)",
    color: "var(--text-primary)",
    verticalAlign: "middle",
    ...extra,
  });

  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    fontFamily: "var(--font, 'Outfit', sans-serif)",
    fontSize: 12,
  };

  // ── Status chip ──────────────────────────────────────────────────────────
  const Chip = ({ label, cc }) => {
    const map = {
      success: { bg: "rgba(74,222,128,0.12)",  color: "var(--color-success)", border: "rgba(74,222,128,0.25)"  },
      warning: { bg: "rgba(245,158,11,0.12)",  color: "var(--color-warning)", border: "rgba(245,158,11,0.25)"  },
      error:   { bg: "rgba(239,68,68,0.12)",   color: "var(--color-danger)",  border: "rgba(239,68,68,0.25)"   },
      accent:  { bg: "rgba(134,96,248,0.12)",  color: "var(--primary)",       border: "rgba(134,96,248,0.25)"  },
    };
    const t = map[cc] || map.accent;
    return (
      <span style={{ background: t.bg, color: t.color, border: `1px solid ${t.border}`, borderRadius: "9999px", padding: "3px 9px", fontSize: 10, fontWeight: 600, fontFamily: "var(--font, 'Outfit', sans-serif)", letterSpacing: "0.02em", display: "inline-block" }}>
        {label}
      </span>
    );
  };

  // ── Ghost button ─────────────────────────────────────────────────────────
  const GhostBtn = ({ children }) => (
    <button style={{ background: "transparent", color: "var(--text-muted)", padding: "6px 14px", borderRadius: 20, fontWeight: 500, fontSize: 12, fontFamily: "var(--font, 'Outfit', sans-serif)", border: "1px solid var(--border)", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, transition: "all 0.2s" }}
      onMouseEnter={e => { e.currentTarget.style.background = "var(--pay-surface-2)"; e.currentTarget.style.color = "var(--text-primary)"; e.currentTarget.style.borderColor = "var(--primary)"; }}
      onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--text-muted)"; e.currentTarget.style.borderColor = "var(--border)"; }}>
      {children}
    </button>
  );

  // ── Row hover handlers ───────────────────────────────────────────────────
  const rowHoverIn  = e => { e.currentTarget.style.background = "rgba(134,96,248,0.04)"; };
  const rowHoverOut = e => { e.currentTarget.style.background = "transparent"; };

  return (
    <div style={{ fontFamily: "var(--font, 'Outfit', sans-serif)", minHeight: "100vh" }}>
      <main style={{ maxWidth: 1600, margin: "0 auto", padding: "32px", display: "flex", flexDirection: "column", gap: 32 }}>

        {/* ── Page Header ── */}
        <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 24 }}>
          <div>
            <h1 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 6px", color: "var(--text-primary)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>Payroll Summary</h1>
            <p style={{ fontSize: 13, margin: 0, color: "var(--text-muted)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>Review, analyze, and finalize payroll for October 2023.</p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button style={{ background: "transparent", color: "var(--text-muted)", padding: "8px 20px", borderRadius: 24, fontWeight: 500, fontSize: 13, fontFamily: "var(--font, 'Outfit', sans-serif)", border: "1px solid var(--border)", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8, transition: "all 0.2s" }}
              onMouseEnter={e => { e.currentTarget.style.background = "var(--pay-surface-2)"; e.currentTarget.style.color = "var(--text-primary)"; e.currentTarget.style.borderColor = "var(--primary)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--text-muted)"; e.currentTarget.style.borderColor = "var(--border)"; }}>
              <i className="fa-solid fa-download" style={{ fontSize: 12 }} /> Export Summary
            </button>
            <button
              onClick={() => onNavigate?.("run-payroll")}
              style={{ background: "var(--primary)", color: "#fff", padding: "8px 20px", borderRadius: 24, fontWeight: 600, fontSize: 13, fontFamily: "var(--font, 'Outfit', sans-serif)", border: "none", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8, transition: "all 0.2s" }}
              onMouseEnter={e => { e.currentTarget.style.background = "var(--primary-hover)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "var(--primary)"; e.currentTarget.style.transform = "translateY(0)"; }}>
              <i className="fa-solid fa-play" style={{ fontSize: 12 }} /> Run Payroll
            </button>
          </div>
        </header>

        {/* ── Filter Bar ── */}
        <div style={{ background: "var(--pay-surface)", border: "1px solid var(--border)", borderRadius: 16, padding: 16, display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>

          {/* Month */}
          <div style={{ flex: "1 1 160px", position: "relative" }}>
            <i className="fa-regular fa-calendar" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: "var(--text-muted)", zIndex: 1, pointerEvents: "none" }} />
            <input
              type="month"
              value={filterMonth}
              onChange={e => setFilterMonth(e.target.value)}
              style={{ width: "100%", background: "var(--input-bg)", border: "1px solid var(--input-border)", borderRadius: "var(--radius-default)", color: "var(--input-color)", padding: "8px 12px 8px 30px", fontSize: 12, fontFamily: "var(--font, 'Outfit', sans-serif)", outline: "none", cursor: "pointer", boxSizing: "border-box" }}
            />
          </div>

          {/* Branch */}
          <div style={{ flex: "1 1 150px", position: "relative" }}>
            <select
              value={filterBranch}
              onChange={e => setFilterBranch(e.target.value)}
              style={{ width: "100%", background: "var(--input-bg)", border: "1px solid var(--input-border)", borderRadius: "var(--radius-default)", color: "var(--input-color)", padding: "8px 32px 8px 12px", fontSize: 12, fontFamily: "var(--font, 'Outfit', sans-serif)", outline: "none", appearance: "none", cursor: "pointer" }}
            >
              {BRANCHES.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
            </select>
            <i className="fa-solid fa-chevron-down" style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", fontSize: 10, color: "var(--text-muted)", zIndex: 1, pointerEvents: "none" }} />
          </div>

          {/* Department */}
          <div style={{ flex: "1 1 150px", position: "relative" }}>
            <select
              value={filterDept}
              onChange={e => setFilterDept(e.target.value)}
              style={{ width: "100%", background: "var(--input-bg)", border: "1px solid var(--input-border)", borderRadius: "var(--radius-default)", color: "var(--input-color)", padding: "8px 32px 8px 12px", fontSize: 12, fontFamily: "var(--font, 'Outfit', sans-serif)", outline: "none", appearance: "none", cursor: "pointer" }}
            >
              {DEPARTMENTS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
            <i className="fa-solid fa-chevron-down" style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", fontSize: 10, color: "var(--text-muted)", zIndex: 1, pointerEvents: "none" }} />
          </div>

          {/* Employee Type */}
          <div style={{ flex: "1 1 150px", position: "relative" }}>
            <select
              value={filterType}
              onChange={e => setFilterType(e.target.value)}
              style={{ width: "100%", background: "var(--input-bg)", border: "1px solid var(--input-border)", borderRadius: "var(--radius-default)", color: "var(--input-color)", padding: "8px 32px 8px 12px", fontSize: 12, fontFamily: "var(--font, 'Outfit', sans-serif)", outline: "none", appearance: "none", cursor: "pointer" }}
            >
              {EMP_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <i className="fa-solid fa-chevron-down" style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", fontSize: 10, color: "var(--text-muted)", zIndex: 1, pointerEvents: "none" }} />
          </div>

          {/* Search */}
          <div style={{ flex: "2 1 220px", position: "relative" }}>
            <i className="fa-solid fa-search" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: "var(--text-muted)", zIndex: 1, pointerEvents: "none" }} />
            <input
              type="text"
              value={filterSearch}
              onChange={e => setFilterSearch(e.target.value)}
              placeholder="Search by Employee, Role or Department…"
              style={{ width: "100%", background: "var(--input-bg)", border: "1px solid var(--input-border)", borderRadius: "var(--radius-default)", color: "var(--input-color)", padding: "8px 12px 8px 38px", fontSize: 12, fontFamily: "var(--font, 'Outfit', sans-serif)", outline: "none", boxSizing: "border-box" }}
            />
          </div>

          {/* Clear button — only shown when a filter is active */}
          {hasActiveFilter && (
            <button
              onClick={clearFilters}
              style={{ flexShrink: 0, padding: "8px 14px", borderRadius: 20, fontSize: 12, fontWeight: 500, background: "rgba(239,68,68,0.08)", color: "var(--color-danger)", border: "1px solid rgba(239,68,68,0.25)", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}
            >
              <i className="fa-solid fa-xmark" style={{ fontSize: 11 }} /> Clear
            </button>
          )}
        </div>
        {/* ── KPI Cards ── */}
        <section style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 18 }}>
          {PS.kpiCards.map((k, i) => (
            <div key={i} style={{ background: "var(--pay-surface)", border: "1px solid var(--border)", borderRadius: 16, padding: 18, position: "relative", overflow: "hidden", transition: "all 0.25s", cursor: "pointer" }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 12px 24px rgba(0,0,0,0.15)"; e.currentTarget.style.borderColor = "var(--primary)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = ""; e.currentTarget.style.borderColor = ""; }}>
              <div style={{ position: "absolute", right: -20, top: -20, width: 90, height: 90, background: k.glow, borderRadius: "50%", filter: "blur(22px)" }} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 500, color: "var(--text-muted)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>
                  <i className={k.icon} style={{ color: k.iconColor, fontSize: 12 }} /> {k.label}
                </div>
                <Chip label={k.badge} cc={k.badgeColor} />
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: "-0.02em", color: "var(--text-primary)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>{k.value}</div>
              <div style={{ fontSize: 10, marginTop: 4, color: "var(--text-muted)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>{k.sub}</div>
            </div>
          ))}
        </section>

        {/* ── Trend + Compliance ── */}
        <section style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 22 }}>
          <div style={{ background: "var(--pay-surface)", border: "1px solid var(--border)", borderRadius: 16, padding: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0, color: "var(--text-primary)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>Payroll Trend Overview</h3>
              <select style={{ padding: "5px 10px", fontSize: 12, background: "var(--input-bg)", border: "1px solid var(--input-border)", borderRadius: 8, color: "var(--input-color)", fontFamily: "var(--font, 'Outfit', sans-serif)", outline: "none" }}>
                <option>Last 6 Months</option><option>YTD</option>
              </select>
            </div>
            <div ref={trendChartRef} style={{ width: "100%", height: 280 }} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={{ background: "var(--pay-surface)", border: "1px solid var(--border)", borderRadius: 16, padding: 22 }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, marginTop: 0, marginBottom: 14, color: "var(--text-primary)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>Compliance Status</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {PS.complianceStatus.map((row, i) => (
                  <div key={i} style={{ background: "var(--pay-surface-2)", border: "1px solid var(--border)", borderRadius: 12, padding: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 30, height: 30, borderRadius: "50%", background: row.ibg, display: "flex", alignItems: "center", justifyContent: "center", color: row.ic }}><i className={row.icon} /></div>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 500, color: "var(--text-primary)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>{row.title}</div>
                        <div style={{ fontSize: 11, color: "var(--text-muted)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>{row.sub}</div>
                      </div>
                    </div>
                    <Chip label={row.label} cc={row.cc} />
                  </div>                ))}
              </div>
            </div>
            <div style={{ background: "var(--pay-surface)", border: "1px solid var(--border)", borderRadius: 16, padding: 22 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0, color: "var(--text-primary)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>Critical Alerts</h3>
                <div style={{ width: 22, height: 22, borderRadius: "50%", background: "rgba(239,68,68,0.15)", color: "var(--color-danger)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, fontFamily: "var(--font, 'Outfit', sans-serif)" }}>2</div>
              </div>
              {PS.criticalAlerts.map((a, i) => (
                <div key={i} style={{ display: "flex", gap: 10, fontSize: 12, borderLeft: `2px solid ${a.bl}`, paddingLeft: 10, marginBottom: i === 0 ? 12 : 0, fontFamily: "var(--font, 'Outfit', sans-serif)" }}>
                  <i className={a.icon} style={{ color: a.ic, marginTop: 2 }} />
                  <div>
                    <span style={{ fontWeight: 600, display: "block", color: "var(--text-primary)" }}>{a.title}</span>
                    <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{a.sub}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Statutory Compliance Cards ── */}
        <section style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 14 }}>
          {PS.statutoryCompliance.map((s, i) => (
            <div key={i} style={{ background: "var(--pay-surface)", border: "1px solid var(--border)", borderRadius: 12, padding: 14, borderLeft: `3px solid ${s.bl}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <div style={{ width: 34, height: 34, borderRadius: 8, background: s.ibg, display: "flex", alignItems: "center", justifyContent: "center", color: s.ic }}><i className={s.icon} style={{ fontSize: 14 }} /></div>
                <div>
                  <h4 style={{ fontWeight: 600, margin: 0, fontSize: 12, color: "var(--text-primary)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>{s.title}</h4>
                  <p style={{ fontSize: 10, margin: 0, color: "var(--text-muted)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>{s.sub}</p>
                </div>
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6, color: "var(--text-primary)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>{s.amount}</div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 9, color: "var(--text-muted)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>{s.due}</span>
                <Chip label={s.status} cc={s.statusColor} />
              </div>
            </div>
          ))}
        </section>

        {/* ── Payout Distribution + Quick Insights ── */}
        <section style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 22 }}>
          <div style={{ background: "var(--pay-surface)", border: "1px solid var(--border)", borderRadius: 16, padding: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 22 }}>
              <div>
                <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0, color: "var(--text-primary)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>Payout Distribution Status</h3>
                <p style={{ fontSize: 11, margin: "4px 0 0", color: "var(--text-muted)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>Real-time payment processing overview</p>
              </div>
              <GhostBtn><i className="fa-solid fa-rotate-right" style={{ fontSize: 11 }} /> Refresh</GhostBtn>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
              {PS.payoutDistribution.map((p, i) => (
                <div key={i}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 36, height: 36, borderRadius: 8, background: p.ibg, display: "flex", alignItems: "center", justifyContent: "center", color: p.ic, fontSize: 13 }}><i className={p.icon} /></div>
                      <div>
                        <div style={{ fontWeight: 500, color: "var(--text-primary)", fontSize: 12, fontFamily: "var(--font, 'Outfit', sans-serif)" }}>{p.label}</div>
                        <div style={{ fontSize: 11, color: "var(--text-muted)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>{p.sub}</div>
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontWeight: 700, color: "var(--text-primary)", fontSize: 12, fontFamily: "var(--font, 'Outfit', sans-serif)" }}>{p.count}</div>
                      <div style={{ fontSize: 11, color: p.ic, fontFamily: "var(--font, 'Outfit', sans-serif)" }}>{p.pl}</div>
                    </div>
                  </div>
                  <div style={{ width: "100%", height: 8, background: "var(--pay-surface-2)", borderRadius: 9999, overflow: "hidden" }}>
                    <div style={{ width: `${p.pct}%`, height: "100%", borderRadius: 9999, background: p.grad }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {PS.quickInsights.map((q, i) => (
              <div key={i} style={{ background: "var(--pay-surface)", border: "1px solid var(--border)", borderRadius: 16, padding: 18, display: "flex", alignItems: "flex-start", gap: 14, cursor: "pointer" }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: q.grad, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: `0 4px 12px ${q.shadow}` }}>
                  <i className={q.icon} style={{ color: "#fff", fontSize: 12 }} />
                </div>
                <div>
                  <div style={{ fontSize: 11, marginBottom: 4, color: "var(--text-muted)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>{q.label}</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: "var(--text-primary)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>{q.value}</div>
                  <div style={{ fontSize: 11, marginTop: 4, color: q.tc, fontFamily: "var(--font, 'Outfit', sans-serif)" }}>{q.trend}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Gross Payroll Cost Breakdown Chart (PIE CHART - MOVED TO TOP) ── */}
        <div style={{ background: "var(--pay-surface)", border: "1px solid var(--border)", borderRadius: 16, padding: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 22 }}>
            <div>
              <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0, color: "var(--text-primary)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>Gross Payroll Cost Breakdown</h3>
              <p style={{ fontSize: 11, margin: "4px 0 0", color: "var(--text-muted)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>Distribution of total payroll components</p>
            </div>
            <select style={{ padding: "5px 10px", fontSize: 12, background: "var(--input-bg)", border: "1px solid var(--input-border)", borderRadius: 8, color: "var(--input-color)", fontFamily: "var(--font, 'Outfit', sans-serif)", outline: "none" }}>
              <option>October 2023</option><option>Q3 2023</option><option>YTD</option>
            </select>
          </div>
          <div ref={breakdownChartRef} style={{ width: "100%", height: 340 }} />
        </div>

        {/* ══════════════════════════════════════════════════════════
            EMPLOYEE PAYROLL STATUS TABLE (BELOW PIE CHART)
        ══════════════════════════════════════════════════════════ */}
        <div style={{ background: "var(--pay-surface)", border: "1px solid var(--border)", borderRadius: 16, overflow: "hidden" }}>
          <div style={{ padding: "20px 22px 16px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 14 }}>
            <div>
              <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0, color: "var(--text-primary)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>Employee Payroll Status</h3>
              <p style={{ fontSize: 12, margin: "3px 0 0", color: "var(--text-muted)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>Detailed view of individual processing states.</p>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <GhostBtn><i className="fa-solid fa-filter" style={{ fontSize: 10 }} /> Filter</GhostBtn>
              <GhostBtn><i className="fa-solid fa-columns" style={{ fontSize: 10 }} /> Columns</GhostBtn>
            </div>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={tableStyle}>
              <thead>
                <tr style={{ background: "var(--pay-surface-2)" }}>
                  <th style={thStyle()}>Employee</th>
                  <th style={thStyle()}>ID &amp; Role</th>
                  <th style={thStyle()}>Gross Pay</th>
                  <th style={thStyle()}>Deductions</th>
                  <th style={thStyle()}>Net Pay</th>
                  <th style={thStyle()}>Status</th>
                  <th style={thStyle(true)}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedEmployees.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ ...tdStyle(), textAlign: "center", padding: "32px 18px", color: "var(--text-muted)" }}>
                      <i className="fa-solid fa-magnifying-glass" style={{ fontSize: 20, marginBottom: 8, display: "block", opacity: 0.4 }} />
                      No employees match the current filters.
                    </td>
                  </tr>
                ) : pagedEmployees.map((row, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid var(--border)", transition: "background 0.15s" }} onMouseEnter={rowHoverIn} onMouseLeave={rowHoverOut}>
                    <td style={tdStyle()}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        {row.avatar
                          ? <img src={row.avatar} alt={row.name} style={{ width: 30, height: 30, borderRadius: "50%", objectFit: "cover", border: "2px solid rgba(134,96,248,0.2)" }} />
                          : <div style={{ width: 30, height: 30, borderRadius: "50%", background: "var(--pay-surface-2)", border: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: "var(--text-muted)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>{row.initials}</div>
                        }
                        <span style={{ fontWeight: 600 }}>{row.name}</span>
                      </div>
                    </td>
                    <td style={tdStyle()}>
                      <div style={{ fontWeight: 600 }}>{row.id}</div>
                      <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{row.role}</div>
                    </td>
                    <td style={tdStyle({ fontWeight: 600 })}>{row.gross}</td>
                    <td style={tdStyle({ color: "var(--color-danger)", fontWeight: 500 })}>{row.deductions}</td>
                    <td style={tdStyle({ fontWeight: 700 })}>{row.net}</td>
                    <td style={tdStyle()}><Chip label={row.status} cc={row.statusColor} /></td>
                    <td style={tdStyle({ textAlign: "right" })}>
                      <button style={{ background: "none", border: "none", cursor: "pointer", padding: "0 6px", color: "var(--text-muted)" }}><i className="fa-solid fa-eye" style={{ fontSize: 12 }} /></button>
                      <button style={{ background: "none", border: "none", cursor: "pointer", padding: "0 6px", color: "var(--text-muted)" }}><i className="fa-solid fa-ellipsis-vertical" style={{ fontSize: 12 }} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ padding: "11px 22px", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 11, fontFamily: "var(--font, 'Outfit', sans-serif)", color: "var(--text-muted)" }}>
            <span>
              {filteredEmployees.length === 0
                ? "No results"
                : `Showing ${empPage * empPageSize + 1}–${Math.min((empPage + 1) * empPageSize, filteredEmployees.length)} of ${filteredEmployees.length} employee${filteredEmployees.length !== 1 ? "s" : ""}`}
            </span>
            <CustomPagination
              total={filteredEmployees.length}
              page={empPage}
              pageSize={empPageSize}
              onPageChange={setEmpPage}
              onPageSizeChange={(s) => { setEmpPageSize(s); setEmpPage(0); }}
            />
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════
            DEPARTMENT-WISE PAYROLL TABLE (BELOW EMPLOYEE TABLE)
        ══════════════════════════════════════════════════════════ */}
        <div style={{ background: "var(--pay-surface)", border: "1px solid var(--border)", borderRadius: 16, overflow: "hidden" }}>
          <div style={{ padding: "20px 22px 16px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 14 }}>
            <div>
              <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0, color: "var(--text-primary)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>Department-wise Payroll Summary</h3>
              <p style={{ fontSize: 12, margin: "3px 0 0", color: "var(--text-muted)", fontFamily: "var(--font, 'Outfit', sans-serif)" }}>Cost allocation across organizational units.</p>
            </div>
            <GhostBtn><i className="fa-solid fa-download" style={{ fontSize: 10 }} /> Export</GhostBtn>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={tableStyle}>
              <thead>
                <tr style={{ background: "#2d1a5e" }}>
                  <th style={thStyle()}>Department</th>
                  <th style={thStyle()}>Employees</th>
                  <th style={thStyle()}>Gross Payroll</th>
                  <th style={thStyle()}>Benefits</th>
                  <th style={thStyle()}>Deductions</th>
                  <th style={thStyle()}>Net Payroll</th>
                  <th style={thStyle()}>Status</th>
                  <th style={thStyle(true)}>% of Total</th>
                </tr>
              </thead>
              <tbody>
                {pagedDepts.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ ...tdStyle(), textAlign: "center", padding: "32px 18px", color: "var(--text-muted)" }}>
                      <i className="fa-solid fa-magnifying-glass" style={{ fontSize: 20, marginBottom: 8, display: "block", opacity: 0.4 }} />
                      No departments match the current filters.
                    </td>
                  </tr>
                ) : pagedDepts.map((d, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid var(--border)", transition: "background 0.15s" }} onMouseEnter={rowHoverIn} onMouseLeave={rowHoverOut}>
                    <td style={tdStyle()}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: d.ibg, display: "flex", alignItems: "center", justifyContent: "center", color: d.ic, fontSize: 13 }}><i className={d.icon} /></div>
                        <span style={{ fontWeight: 600 }}>{d.name}</span>
                      </div>
                    </td>
                    <td style={tdStyle()}>{d.employeeCount}</td>
                    <td style={tdStyle({ fontWeight: 600 })}>{d.gross}</td>
                    <td style={tdStyle({ color: "var(--color-success)", fontWeight: 500 })}>{d.benefits}</td>
                    <td style={tdStyle({ color: "var(--color-danger)", fontWeight: 500 })}>{d.deductions}</td>
                    <td style={tdStyle({ fontWeight: 700 })}>{d.net}</td>
                    <td style={tdStyle()}><Chip label={d.status} cc={d.statusColor} /></td>
                    <td style={tdStyle({ textAlign: "right", fontWeight: 600 })}>{d.pctOfTotal}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ padding: "11px 22px", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 11, fontFamily: "var(--font, 'Outfit', sans-serif)", color: "var(--text-muted)" }}>
            <span>
              {filteredDepts.length === 0
                ? "No results"
                : `Showing ${deptPage * deptPageSize + 1}–${Math.min((deptPage + 1) * deptPageSize, filteredDepts.length)} of ${filteredDepts.length} department${filteredDepts.length !== 1 ? "s" : ""}`}
            </span>
            <CustomPagination
              total={filteredDepts.length}
              page={deptPage}
              pageSize={deptPageSize}
              onPageChange={setDeptPage}
              onPageSizeChange={(s) => { setDeptPageSize(s); setDeptPage(0); }}
            />
          </div>
        </div>

      </main>
    </div>
  );
};

export default PayrollSummary;